/* eslint-disable react-hooks/exhaustive-deps */
import { Card, CardContent, Grid, InputAdornment, TextField } from '@mui/material'
import ProgressBar from 'components/progress/ProgressBar'
import { orderBy, isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useState, createRef, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { getDate } from '../../utils/DateUtil'
import { formatPhone } from '../../utils/StringUtil'
import MessageAction from '../actions/MessageAction'
import { Send } from '@mui/icons-material'
import { CardTitle } from 'components/card/NewCard'

const MessageCard = ({ key, message, updateDate, author }) => (
    <Grid
        key={key}
        container
        size={12}
        justifyContent={author ? 'flex-end' : 'flex-start'}
        spacing={0.3}
    >
        <Card
            sx={{
                maxWidth: '50vw',
                borderRadius: '18px',
                padding: 2,
                color: author ? 'primary.main' : 'mainWhite.main',
                backgroundColor: author ? 'blueStatus.main' : 'primary.main',
            }}
        >
            <Grid size={12} sx={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{message}</Grid>
        </Card>
        <Grid container size={12} justifyContent={author ? 'flex-end' : 'flex-start'} sx={{ color: 'primary.greyBlue' }}>
            {!isEmpty(author) ? `${author.name} (${author.updateLogin}) - `: ''}{getDate(updateDate, 'DD/MM/YYYY - HH[h]mm')}
        </Grid>
    </Grid>
)

MessageCard.propTypes = {
    key: PropTypes.number,
    message: PropTypes.string,
    updateDate: PropTypes.number,
    author: PropTypes.shape({
        name: PropTypes.string,
        updateLogin: PropTypes.string,
    }),
}

const ConversationMessages = ({ login }) => {
    const [newMessage, setNewMessage] = useState('')
    const [dataLoaded, setDataLoaded] = useState(false)
    const endMessages = createRef()

    const {
        listOfMessagesUser,
        users,
        contacts,
    } = useSelector(store => ({
        listOfMessagesUser: store.MessagesReducer.listOfMessagesUser,
        users: store.UserReducer.users,
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)

    const scrollToBottom = () => {
        endMessages.current?.scrollIntoView({ behavior: 'smooth' })
    }

    const dispatch = useDispatch()

    const getMessages = () => {
        dispatch(MessageAction.fetchMessages(login)).then(() =>
            dispatch(MessageAction.fetchLastMessages()).then(() => setDataLoaded(true)),
        )
    }

    useEffect(() => {
        setDataLoaded(false)
        getMessages()
    }, [login])

    useEffect(() => {
        scrollToBottom()
    }, [listOfMessagesUser, dataLoaded])

    const user = useMemo(() => users.find((u) => u.login === login), [users, login])
    const contact = useMemo(() => contacts.find((c) => c.id === user?.contactCode) || {}, [user, contacts])

    const getListOfMessages = () => (orderBy(listOfMessagesUser, 'updateDate').map(({ updateLogin, updateDate, message }, i) => {
        if (login === updateLogin) {
            return (
                <MessageCard
                    key={i}
                    message={message}
                    updateDate={updateDate}
                />
            )
        }
        const updateUser = users.find(u => u.login === updateLogin)
        const updateContact = contacts.find(c => c.id === updateUser?.contactCode)
        return (
            <MessageCard
                key={i}
                author={updateContact ? { name: updateContact.name, updateLogin } : {}}
                message={message}
                updateDate={updateDate}
            />
        )
    }))

    const sendMessage = () => {
        if (newMessage) {
            dispatch(MessageAction.sendMessage({ login, message: newMessage })).then(() => {
                getMessages()
                setNewMessage('')
            })
        }
    }

    return (
        <Card sx={{ width: '100%', height: '100%' }}>
            <CardTitle
                title={`${contact.name ? `${contact.name} (${login})` : login}`}
                subTitle={
                    <Grid container size={12} justifyContent={'flex-end'} fontSize={'1.4rem'}>
                        {`${contact.phoneTel && (contact.mobile || contact.email) ? `${formatPhone(contact.phoneTel)} - ` : (contact.phoneTel ? formatPhone(contact.phoneTel) : '')}
                            ${contact.mobile && contact.email ? `${formatPhone(contact.mobile)} - ` : (contact.mobile ? formatPhone(contact.mobile) : '')}
                            ${contact.email ? contact.email : ''}`}
                    </Grid>
                }
                subtitleSize={'grow'}
            />
            <CardContent>
                <Grid container size={12} padding={1} alignContent={'flex-end'} sx={{ height: 'calc(100% - 60px)' }}>
                    <Grid container size={12} sx={{ maxHeight: 'calc(100% - 69px)', overflowY: 'hidden' }}>
                        <Grid container size={12} spacing={1} padding={1} justifyContent={'center'} alignItems={'center'} sx={{ height: '100%', overflowY: 'auto' }}>
                            {dataLoaded ? getListOfMessages() : <ProgressBar indeterminate />}
                            <Grid ref={ endMessages }/>
                        </Grid>
                    </Grid>
                    <Grid container size={12} alignItems='center' justifyContent='center'>
                        <TextField
                            name='messageField'
                            multiline={true}
                            placeholder={i18n.yourMessage}
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    sendMessage()
                                }
                            }}
                            style={{ width: '90%' }}
                            slotProps={{
                                input: {
                                    endAdornment: <InputAdornment position='end' sx={{ paddingRight: 1, color: newMessage.length ? 'primary.main' : '' }} onClick={newMessage.length ? () => sendMessage() : () => {}}><Send sx={{ fontSize: '3rem', ':hover': newMessage.length ? { cursor: 'pointer' } : {} }}/></InputAdornment>,
                                    style: {
                                        margin: 0,
                                        minHeight: 40,
                                        fontSize: 18,
                                    },
                                },
                            }}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

ConversationMessages.propTypes = {
    login: PropTypes.string,
}

export default ConversationMessages